import moment from "moment-timezone";
import dayjs from "dayjs";
import { getDecryptedUserData } from "./bycrypt";

export const commonDateFormat = "MM-DD-YYYY";
// export const commonDateFormatWithTime = "DD/MM/YYYY - hh:mm A";
export const commonDateFormatWithTime = "MM-DD-YYYY, hh:mm A";
const localTimeZone = () => moment.tz.guess();

export function utcToLocalTime(timeUtc: string | Date, formate = commonDateFormatWithTime) {
	return moment.utc(timeUtc).tz(localTimeZone()).format(formate);
}


export const getBusinessTimeZone = () => {
    const userData = getDecryptedUserData();
    const rawTz = userData?.businessDetails?.timezone || userData?.business?.timezone || userData?.timezone;
    if (rawTz && typeof rawTz === 'string') {
        return rawTz.split(' ')[0];
    }
    return "Europe/London";
};

export const isBusinessTimezoneDST = (date?: any) => {
    const tz = getBusinessTimeZone();
    return (date ? moment(date) : moment()).tz(tz).isDST();
};

export const getBusinessTimezoneOffsetStr = (date?: any) => {
    const tz = getBusinessTimeZone();
    return (date ? moment(date) : moment()).tz(tz).format("Z");
};

export const getBusinessTimezoneAbbr = (date?: any) => {
    const tz = getBusinessTimeZone();
    return (date ? moment(date) : moment()).tz(tz).format("z");
};

export const formatTimeWithBusinessTZ = (date: any, formatStr = "hh:mm A") => {
    if (!date) return "---";
    const tz = getBusinessTimeZone();
    return moment(date).tz(tz).format(formatStr);
};

export const formatDateWithBusinessTZ = (date: any, formatStr = "DD-MM-YYYY") => {
    if (!date) return "---";
    const tz = getBusinessTimeZone();
    return moment(date).tz(tz).format(formatStr);
};

export const formatTimeOffSlot = (timeType: any) => {
    if (typeof timeType === "string" && timeType.includes(":")) {
        const parsed = moment(timeType, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"]);
        if (parsed.isValid()) {
            return parsed.format("hh:mm A");
        }
    }
    return timeType;
};

export const timeOffShiftLabels: Record<number | string, string> = {
    1: "Morning",
    2: "Afternoon",
    3: "Lunchtime",
    4: "End of Day",
};

export const getTimeOffTimeRange = (record: any): string => {
    if (!record?.starting || !record?.ending) return "";

    const startType = record.starting.time_type;
    const endType = record.ending.time_type;

    if (typeof startType === "string" && startType.includes(":")) {
        const startFormatted = formatTimeOffSlot(startType);
        const endFormatted = formatTimeOffSlot(endType);
        return `${startFormatted} to ${endFormatted}`;
    }

    if (startType === 1 && endType === 3) return "Morning to Lunchtime";
    if (startType === 2 && endType === 4) return "Afternoon to End of Day";
    if (startType === 1 && endType === 4) return "Morning to End of Day";

    const startLabel = timeOffShiftLabels[startType] || "";
    const endLabel = timeOffShiftLabels[endType] || "";
    if (startLabel && endLabel) return `${startLabel} to ${endLabel}`;
    return startLabel || endLabel || "";
};

export const formatTimeOffDuration = (record: any, explicitDays?: number) => {
    if (!record) return "-";
    const isHourly =
        record.leave_type === "hours" ||
        (typeof record.starting?.time_type === "string" && record.starting?.time_type.includes(":"));

    if (isHourly) {
        let mins = record.duration_minutes || 0;
        if (!mins && typeof record.starting?.time_type === "string" && typeof record.ending?.time_type === "string") {
            const startM = moment(record.starting.time_type, ["HH:mm", "H:mm"]);
            const endM = moment(record.ending.time_type, ["HH:mm", "H:mm"]);
            if (startM.isValid() && endM.isValid()) {
                mins = Math.max(0, endM.diff(startM, "minutes"));
            }
        }
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        const hhmm = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        return `${hhmm} (Hours)`;
    }

    const startType = record.starting?.time_type;
    const endType = record.ending?.time_type;

    // Descriptive labels for single-day shifts
    if (startType === 1 && endType === 3) return "Half Day (First Half)";
    if (startType === 2 && endType === 4) return "Half Day (Second Half)";
    if (startType === 1 && endType === 4) return "Full Day";

    if (typeof explicitDays === "number") {
        if (explicitDays === 0.5) return "Half Day";
        if (explicitDays === 1) return "Full Day";
        return `${explicitDays} (${explicitDays > 1 ? "Days" : "Day"})`;
    }

    if (!record.starting?.date || !record.ending?.date) return "-";
    let days =
        moment.utc(record.ending.date)
            .startOf("day")
            .diff(moment.utc(record.starting.date).startOf("day"), "days") + 1;

    if (record.starting?.time_type === 2) days -= 0.5;
    if (record.ending?.time_type === 3) days -= 0.5;
    days = days > 0 ? days : 0;

    if (days === 0.5) {
        if (startType === 1 || endType === 3) return "Half Day (First Half)";
        if (startType === 2 || endType === 4) return "Half Day (Second Half)";
        return "Half Day";
    }

    if (days === 1) return "Full Day";

    return `${days} (${days > 1 ? "Days" : "Day"})`;
};

export const getAllowanceDays = (record: any): number | null => {
    if (!record?.starting?.date || !record?.ending?.date) return null;
    let days =
        moment.utc(record.ending.date)
            .startOf("day")
            .diff(moment.utc(record.starting.date).startOf("day"), "days") + 1;

    if (Number(record.starting?.time_type) === 2) days -= 0.5;
    if (Number(record.ending?.time_type) === 3) days -= 0.5;

    return days > 0 ? days : 0;
};

export const getAllowanceDeducted = (record: any): string => {
    if (!record) return "0";

    const val =
        record.allowance_deducted !== undefined && record.allowance_deducted !== null
            ? Number(record.allowance_deducted)
            : record.pto_deducted !== undefined && record.pto_deducted !== null
            ? Number(record.pto_deducted)
            : 0;

    if (isNaN(val) || val <= 0) return "0";
    if (val === 0.5) return "0.5";
    return val.toString();
};

export const formatNotificationText = (text: string) => {
    if (!text || typeof text !== "string") return text;
    // Replace (HH:mm) or (H:mm) or (\bHH:mm\b) that are not already AM/PM
    let formatted = text.replace(/\((\d{1,2}:\d{2})\)/g, (match, timeStr) => {
        const parsed = moment(timeStr, ["HH:mm", "H:mm"]);
        if (parsed.isValid()) {
            return `(${parsed.format("hh:mm A")})`;
        }
        return match;
    });
    formatted = formatted.replace(/\b1\/2 Day\b/g, "Half Day");
    formatted = formatted.replace(/\b1 Day\b/g, "Full Day");
    return formatted;
};

export const convertLocalDayjsToBusinessUTC = (dateObj: any) => {
    if (!dateObj) return null;
    const timeStr = dateObj.format('HH:mm:ss');
    // For pure calendar dates (no explicit time of day), store as pure UTC midnight
    if (timeStr === '00:00:00') {
        return moment.utc(dateObj.format('YYYY-MM-DD')).toISOString();
    }
    const tz = getBusinessTimeZone();
    return moment.tz(dateObj.format('YYYY-MM-DD HH:mm:ss'), tz).toISOString();
};

/**
 * Formats a calendar date (Dayjs, Date, Moment, or string) strictly as pure UTC midnight:
 * "YYYY-MM-DDT00:00:00.000Z" (zero hours, mins, secs, ms, offset).
 * Prevents any timezone conversion or offset bleeding when saving Time Off Requests.
 */
export const formatCalendarDateUTC = (dateObj: any): string => {
    if (!dateObj) return "";
    const str = dayjs.isDayjs(dateObj) || moment.isMoment(dateObj)
        ? dateObj.format("YYYY-MM-DD")
        : String(dateObj).substring(0, 10);
    return `${str}T00:00:00.000Z`;
};

/**
 * Displays a calendar date without applying any timezone offset or shift.
 * Treats the stored date strictly as a pure calendar date.
 */
export const formatCalendarDateDisplay = (dateObj: any, formatStr = "DD-MM-YYYY"): string => {
    if (!dateObj) return "-";
    let str = "";
    if (typeof dateObj === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateObj)) {
        str = dateObj;
    } else {
        str = getEmploymentEndDateString(dateObj) || (typeof dateObj === "string" ? dateObj.substring(0, 10) : moment.utc(dateObj).format("YYYY-MM-DD"));
    }
    return moment.utc(str, "YYYY-MM-DD").format(formatStr);
};

export const convertBusinessUTCToLocalDayjs = (utcStr: string | null | undefined) => {
    if (!utcStr) return null;
    const tz = getBusinessTimeZone();
    return dayjs(moment(utcStr).tz(tz).format('YYYY-MM-DD HH:mm:ss'));
};

export const getFixedAttendanceData = (record: any) => {
    if (!record.attendance || record.attendance.length === 0) return record;

    const sortedAttendance = [...record.attendance].sort((a: any, b: any) => {
        const timeA = formatTimeWithBusinessTZ(a.created_at, "HH:mm:ss");
        const timeB = formatTimeWithBusinessTZ(b.created_at, "HH:mm:ss");
        return timeA.localeCompare(timeB);
    });

    const firstAttendance = sortedAttendance[0];
    const lastAttendance = sortedAttendance[sortedAttendance.length - 1];

    return {
        ...record,
        attendance: sortedAttendance,
        first_clock_in_time: firstAttendance.created_at,
        first_clock_out_time: lastAttendance.clockOut && lastAttendance.updated_at ? lastAttendance.updated_at : null,
    };
};

export interface BookedTimeOffDisplayInfo {
    label: string;
    tooltip?: string;
}

export const getBookedTimeOffDisplay = (record: any, selectedDate?: string | null): BookedTimeOffDisplayInfo => {
    if (!record?.isBookOfTime) return { label: "" };

    const timeOff = record.time_off_request || record.timeOffRequest;
    const st = timeOff?.starting?.time_type;
    const et = timeOff?.ending?.time_type;
    const isHourly =
        timeOff?.leave_type === "hours" ||
        (typeof st === "string" && st.includes(":")) ||
        record.booked_time_off_shift === "Hours";

    let shift = record.booked_time_off_shift || "";
    let range = record.booked_time_off_range || "";

    if (isHourly) {
        if (!range && timeOff) {
            range = getTimeOffTimeRange(timeOff);
        }
        return {
            label: "Booked Time Off (Hours)",
            tooltip: range ? `Booked Time Off (${range})` : "Booked Time Off (Hours)",
        };
    }

    if (!shift && timeOff) {
        if (st === 1 && et === 3) {
            shift = "First Half";
            range = "Morning to Lunchtime";
        } else if (st === 2 && et === 4) {
            shift = "Second Half";
            range = "Afternoon to End of Day";
        } else if (st === 1 && et === 4) {
            shift = "Full Day";
            range = "Morning to End of Day";
        } else {
            const tz = getBusinessTimeZone();
            const startDay = timeOff.starting?.date ? moment.utc(timeOff.starting.date).format("YYYY-MM-DD") : "";
            const endDay = timeOff.ending?.date ? moment.utc(timeOff.ending.date).format("YYYY-MM-DD") : "";
            const currentDay = selectedDate ? (selectedDate.includes("T") ? moment.utc(selectedDate).format("YYYY-MM-DD") : selectedDate.substring(0, 10)) : moment.tz(tz).format("YYYY-MM-DD");

            if (currentDay === startDay && st === 2) {
                shift = "Second Half";
                range = "Afternoon to End of Day";
            } else if (currentDay === endDay && et === 3) {
                shift = "First Half";
                range = "Morning to Lunchtime";
            } else {
                shift = "Full Day";
                range = "Morning to End of Day";
            }
        }
    }

    const label = shift ? `Booked Time Off (${shift})` : "Booked Time Off";
    return {
        label,
        tooltip: range ? `Booked Time Off (${range})` : undefined,
    };
};

export const getBookedTimeOffLabel = (record: any, selectedDate?: string | null): string => {
    return getBookedTimeOffDisplay(record, selectedDate).label;
};

export const formatCreatedLeavesSummary = (
    resData: any
): { createdCount: number; createdDateRange: string } => {
    if (!resData) return { createdCount: 0, createdDateRange: "" };

    const list: any[] = Array.isArray(resData)
        ? resData
        : Array.isArray(resData?.allRequests)
        ? resData.allRequests
        : Array.isArray(resData?.data)
        ? resData.data
        : resData?.starting && resData?.ending
        ? [resData]
        : [];

    const validItems = list.filter((item) => item?.starting?.date);
    if (validItems.length === 0) {
        return { createdCount: 0, createdDateRange: "" };
    }

    const sorted = [...validItems].sort((a, b) => {
        const tA = new Date(a.starting?.date).getTime();
        const tB = new Date(b.starting?.date).getTime();
        return tA - tB;
    });

    const formattedItems = sorted.map((item) => {
        const dateStr = formatCalendarDateDisplay(item.starting.date, "DD-MM-YYYY");
        const st = item.starting?.time_type;
        const et = item.ending?.time_type;

        let durationLabel = "Full Day";
        if (typeof st === "string" && st.includes(":")) {
            const startFormatted = formatTimeOffSlot(st);
            const endFormatted = formatTimeOffSlot(et);
            durationLabel = `${startFormatted} to ${endFormatted}`;
        } else if (
            (st === 1 && et === 3) ||
            (st === 2 && et === 4) ||
            (st === 2 && et === 3)
        ) {
            durationLabel = "Half Day";
        } else if (st === 1 && et === 4) {
            durationLabel = "Full Day";
        }

        return `${dateStr} ${durationLabel}`;
    });

    return {
        createdCount: sorted.length,
        createdDateRange: formattedItems.join(", "),
    };
};

/**
 * Safely extracts the intended calendar date (YYYY-MM-DD) for employment_end_date
 * without business timezone distortion.
 * Accounts for UTC evening shift (e.g. 18:30 UTC when client is in IST).
 */
export const getEmploymentEndDateString = (dateInput: string | Date | any): string => {
    if (!dateInput) return "";
    let m = moment.utc(dateInput);
    if (!m.isValid()) {
        if (typeof dateInput === "string" && dateInput.length >= 10) {
            return dateInput.substring(0, 10);
        }
        return "";
    }
    if (m.hour() >= 12) {
        m = m.add(1, "day");
    }
    return m.format("YYYY-MM-DD");
};

/**
 * Checks if a time off request is scheduled on or after the employee's employment end date.
 * If so, the request cannot be approved or rejected.
 */
export const isTimeOffPastEmploymentEndDate = (record: any): boolean => {
    if (!record) return false;
    const rawEndDate =
        record.employment_end_date ||
        record.user?.employee_details?.basic_details?.employment_end_date ||
        record.employee_details?.basic_details?.employment_end_date ||
        record.user_details?.basic_details?.employment_end_date;

    if (!rawEndDate) return false;

    const empEndDateStr = getEmploymentEndDateString(rawEndDate);
    if (!empEndDateStr) return false;

    const reqStartStr = record.starting?.date
        ? (typeof record.starting.date === "string"
            ? record.starting.date.substring(0, 10)
            : moment.utc(record.starting.date).format("YYYY-MM-DD"))
        : "";

    const reqEndStr = record.ending?.date
        ? (typeof record.ending.date === "string"
            ? record.ending.date.substring(0, 10)
            : moment.utc(record.ending.date).format("YYYY-MM-DD"))
        : reqStartStr;

    if (!reqStartStr) return false;

    return reqStartStr > empEndDateStr || reqEndStr > empEndDateStr;
};

